/**
 * VideoPlaylistFormSection - Video playlist management for events
 * Extracted from EventsTab.jsx
 */
import { Plus, Video, X } from "lucide-react";
import { Button } from "../../ui/button";

// Helper to extract YouTube video ID
const extractYouTubeVideoId = (url) => {
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
};

export const VideoPlaylistFormSection = ({ 
  formData, 
  setFormData, 
  newVideoUrl, 
  setNewVideoUrl, 
  newVideoTitle, 
  setNewVideoTitle 
}) => {
  const addVideoToPlaylist = () => {
    if (!newVideoUrl.trim()) return;
    const videoId = extractYouTubeVideoId(newVideoUrl);
    const thumbnail = videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : "";
    
    setFormData({
      ...formData,
      video_playlist: [
        ...formData.video_playlist,
        {
          id: Date.now().toString(),
          youtube_url: newVideoUrl.trim(),
          title: newVideoTitle.trim() || `Video ${formData.video_playlist.length + 1}`,
          thumbnail_url: thumbnail
        }
      ]
    });
    setNewVideoUrl("");
    setNewVideoTitle("");
  };

  const removeVideoFromPlaylist = (videoId) => {
    setFormData({
      ...formData,
      video_playlist: formData.video_playlist.filter(v => v.id !== videoId)
    });
  };

  return (
    <div className="border-t border-white/10 pt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Video className="w-5 h-5 text-purple-500" />
          <div>
            <h4 className="font-secondary text-sm font-bold uppercase text-white">Video Playlist</h4>
            <p className="text-[10px] text-zinc-500">Add YouTube videos to display on the event page</p>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input 
            type="checkbox" 
            checked={formData.video_playlist_enabled}
            onChange={(e) => setFormData({...formData, video_playlist_enabled: e.target.checked})}
            className="w-4 h-4"
            data-testid="video-playlist-toggle"
          />
          <span className="text-xs text-zinc-400">Enable</span>
        </label>
      </div>

      {formData.video_playlist_enabled && (
        <div className="space-y-4 p-4 bg-purple-500/5 border border-purple-500/20">
          {/* Section Title */}
          <div>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Section Title</label>
            <input 
              type="text"
              value={formData.video_playlist_title}
              onChange={(e) => setFormData({...formData, video_playlist_title: e.target.value})}
              placeholder="Event Videos"
              className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
              data-testid="video-playlist-title"
            />
          </div>

          {/* Add Video Form */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="md:col-span-2">
              <input 
                type="text"
                value={newVideoUrl}
                onChange={(e) => setNewVideoUrl(e.target.value)}
                placeholder="YouTube URL (video or playlist)"
                className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
                data-testid="video-url-input"
              />
            </div>
            <div className="flex gap-2">
              <input 
                type="text"
                value={newVideoTitle}
                onChange={(e) => setNewVideoTitle(e.target.value)}
                placeholder="Title (optional)"
                className="flex-1 bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
              />
              <Button
                type="button"
                onClick={addVideoToPlaylist}
                disabled={!newVideoUrl.trim()}
                className="bg-purple-600 text-white hover:bg-purple-500 text-xs px-3 h-auto rounded-none"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Video List */}
          {formData.video_playlist.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {formData.video_playlist.map((video) => (
                <div key={video.id} className="relative group bg-zinc-900 border border-white/10">
                  {video.thumbnail_url && (
                    <img 
                      src={video.thumbnail_url} 
                      alt={video.title}
                      className="w-full aspect-video object-cover"
                    />
                  )}
                  <div className="p-2">
                    <p className="text-xs text-white truncate">{video.title}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeVideoFromPlaylist(video.id)}
                    className="absolute top-1 right-1 p-1 bg-black/70 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoPlaylistFormSection;
